import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Check, RefreshCw, RotateCcw, AlertCircle, DollarSign } from 'lucide-react';

const TIERS = [
  { id: 'founder', label: 'Founder',  accent: 'text-orange-400' },
  { id: 'growth',  label: 'Growth',   accent: 'text-purple-400' },
  { id: 'ascend',  label: 'Ascend',   accent: 'text-blue-400'   },
];

const COLS = [
  { id: 'seller',   label: 'Seller'         },
  { id: 'growth',   label: 'Growth upline'  },
  { id: 'founder',  label: 'Founder upline' },
  { id: 'platform', label: 'Platform'       },
];

const DEFAULTS = {
  founder: { seller: 50, growth: 0,  founder: 0,  platform: 50 },
  growth:  { seller: 25, growth: 0,  founder: 25, platform: 50 },
  ascend:  { seller: 10, growth: 15, founder: 25, platform: 50 },
};

const sumRow = (row) => Object.values(row || {}).reduce((a, b) => a + (parseFloat(b) || 0), 0);

/** 3-tier × 4-column commission matrix editor for /admin → Commissions tab. */
export default function CommissionMatrixEditor({ getAuthHeader, apiUrl }) {
  const [matrix, setMatrix] = useState(DEFAULTS);
  const [meta, setMeta]     = useState({ updated_at: null, updated_by: null });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${apiUrl}/api/admin/commission-matrix`, { headers: getAuthHeader() });
      setMatrix(r.data?.matrix || DEFAULTS);
      setMeta({ updated_at: r.data?.updated_at, updated_by: r.data?.updated_by });
    } catch (err) {
      toast.error('Could not load matrix');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const update = (tier, col, raw) => {
    const v = raw === '' ? 0 : parseFloat(raw);
    setMatrix(prev => ({ ...prev, [tier]: { ...prev[tier], [col]: isNaN(v) ? 0 : v } }));
  };

  const save = async () => {
    // Local validation: every row must sum to 100
    for (const t of TIERS) {
      const total = sumRow(matrix[t.id]);
      if (Math.abs(total - 100) > 0.01) {
        toast.error(`${t.label} row sums to ${total}% — must equal 100%.`);
        return;
      }
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${apiUrl}/api/admin/commission-matrix`,
        { matrix },
        { headers: getAuthHeader() }
      );
      setMatrix(r.data.matrix);
      setMeta({ updated_at: r.data.updated_at, updated_by: meta.updated_by });
      toast.success('Commission matrix updated. New sales will use these splits immediately.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset to platform defaults (Founder 50/50, Growth 25/0/25/50, Ascend 10/15/25/50)?')) return;
    try {
      const r = await axios.post(`${apiUrl}/api/admin/commission-matrix/reset`, {}, { headers: getAuthHeader() });
      setMatrix(r.data.matrix || DEFAULTS);
      setMeta({ updated_at: null, updated_by: null });
      toast.success('Reset to defaults.');
    } catch (err) {
      toast.error('Reset failed');
    }
  };

  if (loading) {
    return <p className="text-muted-foreground"><RefreshCw className="w-4 h-4 inline animate-spin mr-2" /> Loading…</p>;
  }

  return (
    <div className="space-y-6" data-testid="commission-matrix-editor">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-playfair font-bold text-foreground mb-1 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-emerald-500" /> Sales Commission Matrix
          </h2>
          <p className="text-muted-foreground text-sm max-w-3xl">
            Multi-level split for <strong>listing-sale uploads</strong>. Each row is what happens when a member of
            that tier records a sale. Each row must sum to <strong>100%</strong>. Missing-tier shares (no upline of that
            tier in the chain) automatically roll up to the platform.
          </p>
          {meta.updated_at && (
            <p className="text-xs text-muted-foreground mt-1">
              Last edited {new Date(meta.updated_at).toLocaleString()}
            </p>
          )}
        </div>
        <Button variant="outline" onClick={reset} data-testid="reset-matrix-btn">
          <RotateCcw className="w-4 h-4 mr-2" /> Reset to defaults
        </Button>
      </div>

      <div className="wm-card rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-input/40">
            <tr>
              <th className="text-left p-3 text-foreground">Sale by</th>
              {COLS.map(c => <th key={c.id} className="text-right p-3 text-foreground">{c.label}</th>)}
              <th className="text-right p-3 text-foreground">Total</th>
            </tr>
          </thead>
          <tbody>
            {TIERS.map(t => {
              const row = matrix[t.id] || DEFAULTS[t.id];
              const total = sumRow(row);
              const ok = Math.abs(total - 100) <= 0.01;
              return (
                <tr key={t.id} className="border-t border-border" data-testid={`matrix-row-${t.id}`}>
                  <td className={`p-3 font-semibold ${t.accent}`}>{t.label}</td>
                  {COLS.map(c => (
                    <td key={c.id} className="p-2 text-right">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={row[c.id]}
                        onChange={(e) => update(t.id, c.id, e.target.value)}
                        className="bg-input text-foreground text-right w-20 ml-auto"
                        data-testid={`matrix-${t.id}-${c.id}`}
                      />
                    </td>
                  ))}
                  <td className={`p-3 text-right font-mono font-bold ${ok ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {total.toFixed(1)}%
                    {!ok && <AlertCircle className="w-4 h-4 inline ml-1" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Button
        onClick={save}
        disabled={saving}
        className="bg-emerald-600 hover:bg-emerald-700 text-white"
        data-testid="save-matrix-btn"
      >
        {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
        Save Commission Matrix
      </Button>

      <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-4 text-sm text-muted-foreground">
        <h4 className="font-semibold text-emerald-400 mb-2">How the matrix works</h4>
        <ul className="space-y-1.5">
          <li>- The <strong>Sale by</strong> tier is the seller making the sale (where their referral code is the seller's own code).</li>
          <li>- <strong>Growth upline</strong> = the first Growth member found by walking the seller's <code>referred_by</code> chain upward.</li>
          <li>- <strong>Founder upline</strong> = the first Founder member found in that same chain.</li>
          <li>- Only Growth + Founder hold referral codes that recruit. Ascend members get a code for tracking their own sales only.</li>
          <li>- If an upline tier is missing from the chain (e.g. an Ascend recruited directly by a Founder, no Growth in between), that share rolls into the Platform column for that sale.</li>
          <li>- This matrix only affects <strong>new sales</strong>; previously-credited sales are not retroactively rebalanced.</li>
          <li>- Course purchases + service bookings still use the legacy 75 / 10 / 15 (creator / upline / platform) split.</li>
        </ul>
      </div>
    </div>
  );
}
